import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SocialIconsRow } from './SocialIcons';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const location = useLocation();

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Close dropdown on route change
  useEffect(() => {
    setDesktopServicesOpen(false);
  }, [location]);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-lotus-petal/60 bg-white/90 backdrop-blur-md transition-all duration-300">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" className="flex items-center gap-3.5 group shrink-0">
            <motion.img
              whileHover={{ scale: 1.08, rotate: 3 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              src="/images/lotus-prana-logo.png"
              alt="Lotus Prana Logo"
              className="h-10 w-auto object-contain rounded-full shadow-sm"
            />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-2xl tracking-tight text-lotus-ink font-medium group-hover:text-lotus-deep transition-colors">
                Lotus Prana
              </span>
              <span className="mt-1 text-[10px] uppercase tracking-[0.2em] text-lotus-deep/80 font-medium">
                Energy & Wellbeing
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-6 text-xs font-medium uppercase tracking-wider text-lotus-ink md:flex lg:gap-8">
            <Link
              to="/#story"
              className="relative transition-colors hover:text-lotus-deep whitespace-nowrap py-1 group"
            >
              Our Story
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-lotus-deep transition-all duration-300 group-hover:w-full" />
            </Link>

            <Link
              to="/#about"
              className="relative transition-colors hover:text-lotus-deep whitespace-nowrap py-1 group"
            >
              About Us
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-lotus-deep transition-all duration-300 group-hover:w-full" />
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDesktopServicesOpen(true)}
              onMouseLeave={() => setDesktopServicesOpen(false)}
            >
              <button
                onClick={() => setDesktopServicesOpen(!desktopServicesOpen)}
                className="flex items-center gap-1.5 transition-colors hover:text-lotus-deep whitespace-nowrap py-1 uppercase tracking-wider text-xs font-medium focus:outline-none"
              >
                <span>Services</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${
                    desktopServicesOpen ? 'rotate-180 text-lotus-deep' : ''
                  }`}
                />
              </button>

              <AnimatePresence>
                {desktopServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="absolute left-0 top-full pt-2 w-64 z-50"
                  >
                    <div className="rounded-2xl bg-white p-3 shadow-xl border border-lotus-petal/80 flex flex-col gap-1 backdrop-blur-lg">
                      <Link
                        to="/#services"
                        onClick={() => setDesktopServicesOpen(false)}
                        className="rounded-xl px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-lotus-ink hover:bg-lotus-petal/40 hover:text-lotus-deep transition-all transform hover:translate-x-1"
                      >
                        Services Overview
                      </Link>
                      <Link
                        to="/individual-wellness"
                        onClick={() => setDesktopServicesOpen(false)}
                        className="rounded-xl px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-lotus-ink hover:bg-lotus-petal/40 hover:text-lotus-deep transition-all transform hover:translate-x-1"
                      >
                        Individual Wellness
                      </Link>
                      <Link
                        to="/corporate-wellness"
                        onClick={() => setDesktopServicesOpen(false)}
                        className="rounded-xl px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-lotus-ink hover:bg-lotus-petal/40 hover:text-lotus-deep transition-all transform hover:translate-x-1"
                      >
                        Corporate Wellness
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/#philosophy"
              className="relative transition-colors hover:text-lotus-deep whitespace-nowrap py-1 group"
            >
              Our Philosophy
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-lotus-deep transition-all duration-300 group-hover:w-full" />
            </Link>

            <Link
              to="/#testimonials"
              className="relative transition-colors hover:text-lotus-deep whitespace-nowrap py-1 group"
            >
              Testimonials
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-lotus-deep transition-all duration-300 group-hover:w-full" />
            </Link>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/#contact"
                className="rounded-full bg-lotus-deep px-5 py-2.5 text-white transition-all hover:bg-lotus-deep/90 shadow-sm hover:shadow-md whitespace-nowrap inline-block"
              >
                Contact
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Toggle Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-lotus-ink md:hidden focus:outline-none transition-colors hover:text-lotus-deep rounded-full active:bg-lotus-petal/40"
            aria-label="Open navigation menu"
          >
            <Menu size={26} />
          </motion.button>
        </div>
      </header>

      {/* Mobile Drawer & Backdrop with AnimatePresence */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Mobile Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs md:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Mobile Drawer Sidebar */}
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed top-0 right-0 z-50 h-full h-screen w-[82%] max-w-sm bg-white shadow-2xl md:hidden flex flex-col" style={{zIndex: 99999}}
            >
              {/* Sidebar Header */}
              <div className="flex items-center justify-between border-b border-lotus-petal/60 px-6 py-5">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3">
                  <img
                    src="/images/lotus-prana-logo.png"
                    alt="Lotus Prana Logo"
                    className="h-9 w-auto object-contain rounded-full"
                  />
                  <div className="flex flex-col leading-none">
                    <span className="font-serif text-xl tracking-tight text-lotus-ink font-medium">
                      Lotus Prana
                    </span>
                    <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-lotus-deep/80 font-medium">
                      Energy & Wellbeing
                    </span>
                  </div>
                </Link>
                <motion.button
                  whileTap={{ scale: 0.9, rotate: 90 }}
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-full text-lotus-ink hover:text-lotus-deep hover:bg-lotus-petal/50 transition-colors focus:outline-none"
                  aria-label="Close navigation menu"
                >
                  <X size={24} />
                </motion.button>
              </div>

              {/* Sidebar Navigation Body */}
              <div className="flex-1 overflow-y-auto px-6 py-8 flex flex-col justify-between">
                <nav className="flex flex-col gap-5 text-sm uppercase tracking-widest font-medium text-lotus-ink">
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}>
                    <Link
                      to="/#story"
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 block transition-colors hover:text-lotus-deep active:translate-x-1 transform transition-transform"
                    >
                      Our Story
                    </Link>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                    <Link
                      to="/#about"
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 block transition-colors hover:text-lotus-deep active:translate-x-1 transform transition-transform"
                    >
                      About Us
                    </Link>
                  </motion.div>

                  {/* Mobile Services Accordion */}
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }} className="flex flex-col gap-2 py-1">
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="flex items-center justify-between text-sm uppercase tracking-widest font-medium text-lotus-ink hover:text-lotus-deep focus:outline-none w-full text-left"
                    >
                      <span>Services</span>
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-300 ${
                          mobileServicesOpen ? 'rotate-180 text-lotus-deep' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="flex flex-col gap-3 pl-4 mt-2 border-l-2 border-lotus-petal text-xs font-medium uppercase tracking-wider overflow-hidden"
                        >
                          <Link
                            to="/#services"
                            onClick={() => setMobileMenuOpen(false)}
                            className="py-1 text-lotus-ink/80 hover:text-lotus-deep active:translate-x-1 transition-transform block"
                          >
                            Services Overview
                          </Link>
                          <Link
                            to="/individual-wellness"
                            onClick={() => setMobileMenuOpen(false)}
                            className="py-1 text-lotus-ink/80 hover:text-lotus-deep active:translate-x-1 transition-transform block"
                          >
                            Individual Wellness
                          </Link>
                          <Link
                            to="/corporate-wellness"
                            onClick={() => setMobileMenuOpen(false)}
                            className="py-1 text-lotus-ink/80 hover:text-lotus-deep active:translate-x-1 transition-transform block"
                          >
                            Corporate Wellness
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                    <Link
                      to="/#philosophy"
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 block transition-colors hover:text-lotus-deep active:translate-x-1 transform transition-transform"
                    >
                      Our Philosophy
                    </Link>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                    <Link
                      to="/#testimonials"
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 block transition-colors hover:text-lotus-deep active:translate-x-1 transform transition-transform"
                    >
                      Testimonials
                    </Link>
                  </motion.div>
                </nav>

                {/* Sidebar CTA Button & Social Icons */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.3 }}
                  className="mt-8 pt-6 border-t border-lotus-petal/60 flex flex-col gap-5"
                >
                  <Link
                    to="/#contact"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full text-center rounded-full bg-lotus-deep py-3.5 px-6 text-white text-sm font-semibold uppercase tracking-wider transition-all hover:bg-lotus-deep/90 shadow-md hover:shadow-lg active:scale-95 touch-bounce"
                  >
                    Contact
                  </Link>

                  <div className="pt-1">
                    <SocialIconsRow containerClass="flex items-center justify-center gap-4" />
                  </div>
                </motion.div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;


